// SCR-STAFF-1006 — اعتبارنامه‌های من نزد بیمه‌گر (US-INT-112، 113؛ AP-INT-10): کارت هر بیمه‌گر با وضعیت نشست، زمان انقضا،
// چهار رقم آخر کد ملی ثبت‌شده، «اتصال» (هدایت به صفحه رسمی بیمه‌گر — گذرواژه هرگز وارد ققنوس نمی‌شود، BR-INT-105) و
// «قطع اتصال» (حذف توکن از مخزن امن و خروج از بیمه‌گر). هیچ توکنی در این صفحه دیده نمی‌شود (BR-INT-106).
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type InsurerCredentialView, type InsurerSessionStatus, type InsurerSubjectRole } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  ErrorState,
  JalaliDate,
  ListPage,
  LoadingState,
  StatusChip,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { ProviderCredentialMatrix } from './ProviderCredentialMatrix';
import { intKeys } from './common';

export const SCR_STAFF_1006 = 'SCR-STAFF-1006';

/** نقش نشست نزد بیمه‌گر (AC-INT-113.1). */
const SUBJECT_ROLES: readonly InsurerSubjectRole[] = ['Provider', 'Receptionist', 'Nurse'];

const STATUS_TONE: Record<InsurerSessionStatus, StatusTone> = {
  NotConfigured: 'inactive',
  Active: 'active',
  Expired: 'error',
  Invalid: 'error',
};

export function InsurerCredentialsPage() {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('int.credential.manage_own');
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [role, setRole] = useState<InsurerSubjectRole>('Provider');
  const [disconnecting, setDisconnecting] = useState<InsurerCredentialView | null>(null);

  const credentials = useQuery({
    queryKey: intKeys.insurerCredentials(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/integration/insurer-credentials')),
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: intKeys.insurerCredentials(session?.tenantId) });

  const connect = useMutation({
    mutationFn: (insurer: string) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/insurer-credentials/{insurer}:connect', {
          params: { path: { insurer } },
          body: { subjectRole: role, returnUrl: window.location.pathname },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    // AC-INT-112.1: ورود در صفحه رسمی بیمه‌گر انجام می‌شود، نه در ققنوس.
    onSuccess: (result) => window.location.assign(result.redirectUrl),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
  });

  const disconnect = useMutation({
    mutationFn: (insurer: string) =>
      unwrap(
        tenantApi.POST('/api/v1/integration/insurer-credentials/{insurer}:disconnect', {
          params: { path: { insurer } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSuccess: (result) => {
      setDisconnecting(null);
      invalidate();
      // AC-INT-112.4: خروج از نشست بیمه‌گر در مرورگر هم انجام می‌شود.
      window.location.assign(result.signOutUrl);
    },
  });

  if (credentials.isPending) {
    return <LoadingState />;
  }

  if (credentials.error) {
    return <ErrorState error={credentials.error} onRetry={() => void credentials.refetch()} />;
  }

  return (
    <ListPage
      screenId={SCR_STAFF_1006}
      titleKey="int:insurer_credentials.title"
      subtitleKey="int:insurer_credentials.subtitle"
      filters={
        <Stack spacing={1}>
          <TextField
            select
            size="small"
            label={t('int:insurer_credentials.subject_role')}
            value={role}
            onChange={(event) => setRole(event.target.value as InsurerSubjectRole)}
            sx={{ maxWidth: 260 }}
            slotProps={{ htmlInput: { 'data-testid': 'insurer-subject-role' } }}
          >
            {SUBJECT_ROLES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`int:insurer_credentials.roles.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <WriteNotice write={write} />
          <Alert severity="info">{t('int:insurer_credentials.password_hint')}</Alert>
          {connect.error ? <ErrorState error={connect.error} compact /> : null}
        </Stack>
      }
    >
      <Stack spacing={2}>
        {(credentials.data?.items ?? []).map((item) => (
          <Paper
            key={item.insurer}
            variant="outlined"
            sx={{ p: 2 }}
            data-testid={`insurer-card-${item.insurer}`}
            data-status={item.status}
          >
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1">
                  {t(`int:insurer_credentials.insurer.${item.insurer}`, { defaultValue: item.insurer })}
                </Typography>
                <span data-testid={`insurer-status-${item.insurer}`}>
                  <StatusChip
                    tone={STATUS_TONE[item.status]}
                    labelKey={`int:insurer_credentials.status.${item.status}`}
                  />
                </span>
              </Stack>

              {item.status === 'NotConfigured' ? (
                <Typography variant="body2" color="text.secondary">
                  {t('int:insurer_credentials.not_connected')}
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    {t(`int:insurer_credentials.roles.${item.subjectRole}`)}
                    {item.audienceLast4 ? (
                      <>
                        {t('common:list_separator')}
                        {t('int:insurer_credentials.audience_last4', { last4: item.audienceLast4 })}
                      </>
                    ) : null}
                  </Typography>
                  {item.expiresAt ? (
                    <Typography variant="body2" color="text.secondary" data-testid={`insurer-expires-${item.insurer}`}>
                      {t('int:insurer_credentials.expires_at')} <JalaliDate value={item.expiresAt} withTime />
                    </Typography>
                  ) : null}
                  {item.connectedAt ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('int:insurer_credentials.connected_at')} <JalaliDate value={item.connectedAt} withTime />
                    </Typography>
                  ) : null}
                  {item.lastErrorCode ? (
                    <Alert severity="warning" data-testid={`insurer-error-${item.insurer}`}>
                      {t('int:insurer_credentials.last_error', { code: item.lastErrorCode })}
                    </Alert>
                  ) : null}
                </Stack>
              )}

              {!item.canConnect ? (
                <Alert severity="warning" data-testid={`insurer-not-configured-${item.insurer}`}>
                  {t('int:insurer_credentials.platform_not_configured')}
                </Alert>
              ) : null}

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<LoginOutlinedIcon />}
                  onClick={() => connect.mutate(item.insurer)}
                  disabled={!write.canWrite || !item.canConnect || connect.isPending}
                  data-testid={`insurer-connect-${item.insurer}`}
                >
                  {item.status === 'NotConfigured'
                    ? t('int:insurer_credentials.connect')
                    : t('int:insurer_credentials.reconnect')}
                </Button>
                {item.status === 'NotConfigured' ? null : (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOffOutlinedIcon />}
                    onClick={() => setDisconnecting(item)}
                    disabled={!write.canWrite}
                    data-testid={`insurer-disconnect-${item.insurer}`}
                  >
                    {t('int:insurer_credentials.disconnect')}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
      {/* AP-INT-09 (US-INT-104 تا 108): ماتریس بیمه‌گر × شعبه و ورود سه‌روشی بیمه‌گرهای غیر تامین. */}
      <ProviderCredentialMatrix />
      <ConfirmDialog
        open={disconnecting !== null}
        titleKey="int:insurer_credentials.disconnect_title"
        messageKey="int:insurer_credentials.disconnect_body"
        confirmKey="int:insurer_credentials.disconnect"
        destructive
        busy={disconnect.isPending}
        error={disconnect.error}
        onCancel={() => setDisconnecting(null)}
        onConfirm={() => {
          if (disconnecting) {
            disconnect.mutate(disconnecting.insurer);
          }
        }}
      />
    </ListPage>
  );
}
